import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  Timeline as TimelineIcon,
  ArrowForward,
} from '@mui/icons-material';

/**
 * A single pipeline stage block.
 */
const PipelineStage = ({ label, count, color, isLast = false }) => (
  <div className="flex items-center">
    <Tooltip title={`${count} application${count !== 1 ? 's' : ''} in ${label}`} arrow>
      <div
        className="flex flex-col items-center justify-center rounded-xl px-4 py-3 min-w-[90px] cursor-default
                   hover:scale-105 transition-transform duration-200"
        style={{ backgroundColor: `${color}12`, border: `1px solid ${color}30` }}
      >
        <span className="text-2xl font-bold" style={{ color }}>
          {count}
        </span>
        <span className="text-xs font-medium text-gray-600 mt-0.5 whitespace-nowrap">
          {label}
        </span>
      </div>
    </Tooltip>
    {!isLast && (
      <ArrowForward sx={{ fontSize: 18, color: '#d1d5db', mx: 0.5, flexShrink: 0 }} />
    )}
  </div>
);

/**
 * CreditPipelinePanel
 *
 * Visual credit pipeline showing stages:
 *   Applied → Approved → Disbursed → Active → Settled
 */
const CreditPipelinePanel = ({ data = {}, loading = false }) => {
  const stages = [
    { label: 'Applied', count: data.applied ?? 0, color: '#6366f1' },
    { label: 'Approved', count: data.approved ?? 0, color: '#0ea5e9' },
    { label: 'Disbursed', count: data.disbursed ?? 0, color: '#10b981' },
    { label: 'Active', count: data.active ?? 0, color: '#f59e0b' },
    { label: 'Settled', count: data.settled ?? 0, color: '#8b5cf6' },
  ];

  const total = stages.reduce((sum, s) => sum + s.count, 0);

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 animate-pulse min-h-[280px]">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-6" />
        <div className="flex gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="w-20 h-20 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-md transition-shadow duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center">
            <TimelineIcon sx={{ fontSize: 20, color: '#0ea5e9' }} />
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Credit Pipeline</h3>
        </div>
        <Chip
          label={`${total} total`}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
        />
      </div>

      {/* Pipeline stages */}
      <div className="flex items-center justify-between overflow-x-auto pb-2">
        {stages.map((stage, idx) => (
          <PipelineStage
            key={stage.label}
            {...stage}
            isLast={idx === stages.length - 1}
          />
        ))}
      </div>

      {/* Conversion funnel */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-3">
          <div className="text-xs">
            <span className="text-gray-400">Approval Rate</span>
            <p className="font-semibold text-gray-700">
              {data.applied > 0
                ? `${(((data.approved ?? 0) / data.applied) * 100).toFixed(1)}%`
                : 'N/A'}
            </p>
          </div>
          <div className="text-xs">
            <span className="text-gray-400">Settlement Rate</span>
            <p className="font-semibold text-gray-700">
              {data.active > 0 || data.settled > 0
                ? `${(((data.settled ?? 0) / ((data.active ?? 0) + (data.settled ?? 0))) * 100).toFixed(1)}%`
                : 'N/A'}
            </p>
          </div>
          <div className="text-xs">
            <span className="text-gray-400">Rejected</span>
            <p className="font-semibold text-red-500">{data.rejected ?? 0}</p>
          </div>
          <div className="text-xs">
            <span className="text-gray-400">Defaulted</span>
            <p className="font-semibold text-red-500">{data.defaulted ?? 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPipelinePanel;
